import { isNumber, round } from "lodash";
import { EPayoutReferenceType, } from "../../forms/createPayment/schema.createPayment.types";
import { getCanIndex } from "../../reduxV2/contract/contract.utils";
import { EContractCostsAreIndexable, EContractCostsPriceType, } from "../../types/contract.types";
import { EPaymentMethod, EPaymentOrderOwnerPaymentMethod, EPaymentOrderType, EPaymentRepetitionType, } from "../../types/payment.types";
import { getDaysInMonth } from "../../utils/date-fns.utils";
import { getProRataDays } from "./schema.createPayment.3";
import { ECreatePaymentMethod, EField } from "./schema.createPayment.types";
import { checkIfTypeIsAdditionalCost, checkIfTypeIsOther, getFirstPayDateAt, getRelatedContract, getRepetitionDay, getStartDate, hasProRata, isNewContract, isRepetitionDelayed, proRataForContract, shouldAskOwnerPaymentMethod, } from "./schema.createPayment.utils";
export var getPriceType = function (formData, contract) {
    if (!contract) {
        return;
    }
    var type = formData[EField.TypeOnce] || formData[EField.TypeRecurring];
    switch (true) {
        case type === EPaymentOrderType.CommonCosts:
            return contract.priceTypeCommonCosts;
        case type === EPaymentOrderType.NonCommonCosts:
            return contract.priceTypeNonCommonCosts;
        default:
            return;
    }
};
export var getAmount = function (formData) {
    var type = formData[EField.TypeOnce] || formData[EField.TypeRecurring];
    switch (type) {
        case EPaymentOrderType.CommonCosts:
            return formData[EField.PriceCommonCosts] || 0;
        case EPaymentOrderType.NonCommonCosts:
            return formData[EField.PriceNonCommonCosts] || 0;
        default:
            return formData[EField.Price] || 0;
    }
};
export var getVat = function (formData, contract) {
    var type = formData[EField.TypeOnce] || formData[EField.TypeRecurring];
    switch (type) {
        case EPaymentOrderType.Rent:
        case EPaymentOrderType.RentAndCosts: {
            var vatUnmapped = (contract ? contract.priceVat : formData[EField.PriceVat]) || 0;
            var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
            var noVatType = vat === 0 ? contract === null || contract === void 0 ? void 0 : contract.priceNoVatType : undefined;
            return {
                vat: vat,
                noVatType: noVatType,
            };
        }
        case EPaymentOrderType.CommonCosts: {
            var vatUnmapped = (contract
                ? contract.costsCommonVat
                : formData[EField.PriceCommonCostsVat]) || 0;
            var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
            var noVatType = vat === 0 ? contract === null || contract === void 0 ? void 0 : contract.costsCommonNoVatType : undefined;
            return {
                vat: vat,
                noVatType: noVatType,
            };
        }
        case EPaymentOrderType.NonCommonCosts: {
            var vatUnmapped = (contract
                ? contract.costsNonCommonVat
                : formData[EField.PriceNonCommonCostsVat]) || 0;
            var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
            var noVatType = vat === 0 ? contract === null || contract === void 0 ? void 0 : contract.costsNonCommonNoVatType : undefined;
            return {
                vat: vat,
                noVatType: noVatType,
            };
        }
        default: {
            var vatUnmapped = formData[EField.PriceVat] || 0;
            var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
            var noVatType = !isNumber(vatUnmapped)
                ? vatUnmapped
                : undefined;
            return {
                vat: vat,
                noVatType: noVatType,
            };
        }
    }
};
var getPreferredPaymentMethods = function (formData) {
    var createPaymentMethods = formData[EField.PaymentMethods] || [];
    var result = [];
    createPaymentMethods.forEach(function (createPaymentMethod) {
        switch (createPaymentMethod) {
            case ECreatePaymentMethod.SepaOnce:
                result.push(EPaymentMethod.SepaOnce);
                break;
            case ECreatePaymentMethod.SepaRecurring:
                result.push(EPaymentMethod.SepaRecurring);
                break;
            case ECreatePaymentMethod.Online:
                result.push.apply(result, [EPaymentMethod.Bancontact, EPaymentMethod.Ideal]);
                break;
            case ECreatePaymentMethod.Ibanisation:
            default:
                break;
        }
    });
    return result;
};
export var getPaymentGroup = function (formData, propertyId, extraData, paymentOrder) {
    var contract = getRelatedContract(extraData, formData);
    var contractId = contract === null || contract === void 0 ? void 0 : contract.id;
    var startDate = getStartDate(formData);
    var repetitionDay = getRepetitionDay(startDate, contract);
    var startedAt = startDate || new Date();
    var repetitionType = EPaymentRepetitionType.Once;
    if (formData[EField.TypeRecurring]) {
        repetitionType = EPaymentRepetitionType.Monthly;
        if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
            repetitionType =
                contract.repetitionType;
        }
        if (formData[EField.TypeRecurring] === EPaymentOrderType.OtherRecurring) {
            repetitionType = formData[EField.PaymentRepetition];
        }
    }
    var preferredPaymentMethods = getPreferredPaymentMethods(formData);
    if (formData[EField.OwnerPaymentMethod]) {
        var shouldAsk = shouldAskOwnerPaymentMethod(formData, extraData);
        if (shouldAsk) {
            if (formData[EField.OwnerPaymentMethod] ===
                EPaymentOrderOwnerPaymentMethod.FromRent) {
                // undefined => don't update preferredPaymentMethods
                preferredPaymentMethods = undefined;
            }
        }
    }
    var paymentGroup = {
        startedAt: startedAt,
        repetitionDay: repetitionDay,
        propertyId: propertyId,
        repetitionType: repetitionType,
        preferredPaymentMethods: preferredPaymentMethods,
        contractId: contractId,
    };
    return paymentGroup;
};
export var getPaymentOrderV2 = function (formData, extraData) {
    var contract = getRelatedContract(extraData, formData);
    var type = formData[EField.TypeOnce] || formData[EField.TypeRecurring];
    if (type === EPaymentOrderType.RentAndCosts) {
        type = EPaymentOrderType.Rent;
    }
    var amount = getAmount(formData);
    var _a = getVat(formData, contract), vat = _a.vat, noVatType = _a.noVatType;
    var proRataAmount = 0;
    if (formData[EField.TypeRecurring]) {
        if (hasProRata(formData, contract)) {
            proRataAmount = formData[EField.FirstPrice] || 0;
            if (type === EPaymentOrderType.CommonCosts) {
                proRataAmount = formData[EField.FirstPriceCommonCosts];
            }
            else if (type === EPaymentOrderType.NonCommonCosts) {
                proRataAmount = formData[EField.FirstPriceNonCommonCosts];
            }
            // INFO: When repetition is delayed, subtract amount
            if (isRepetitionDelayed(formData, contract) &&
                !!formData[EField.TypeRecurring]) {
                proRataAmount = proRataAmount - amount;
            }
        }
        // Calculate pro rata for old contracts (Needed for out-going pro-rata at end contract)
        else if (contract && !isNewContract(contract)) {
            proRataAmount = proRataForContract(contract, formData[EField.Price]);
        }
    }
    var typePayment = getPriceType(formData, contract);
    var payerId = formData[EField.Payer]
        ? formData[EField.Payer][0]
        : undefined;
    var payeeId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeBankAccountId = formData[EField.Iban];
    var otherDescription = checkIfTypeIsOther(formData)
        ? formData[EField.OtherDescription]
        : undefined;
    var repetitionType = EPaymentRepetitionType.Once;
    if (formData[EField.TypeRecurring]) {
        repetitionType = EPaymentRepetitionType.Monthly;
        if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
            repetitionType =
                contract.repetitionType;
        }
        if (formData[EField.TypeRecurring] === EPaymentOrderType.OtherRecurring) {
            repetitionType = formData[EField.PaymentRepetition];
        }
    }
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: repetitionType,
        formData: formData,
        contract: contract,
    });
    var ownerPaymentMethod = EPaymentOrderOwnerPaymentMethod.Direct;
    if (formData[EField.OwnerPaymentMethod]) {
        var shouldAsk = shouldAskOwnerPaymentMethod(formData, extraData);
        if (shouldAsk) {
            ownerPaymentMethod = formData[EField.OwnerPaymentMethod];
        }
    }
    if (checkIfTypeIsAdditionalCost(formData)) {
        type = EPaymentOrderType.AdditionalCosts;
    }
    var result = {
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: proRataAmount,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        type: type,
        typePayment: typePayment,
        ownerPaymentMethod: ownerPaymentMethod,
        payDateOffset: parseInt(formData[EField.PaydateOffset], 0),
        firstPayDateAt: firstPayDateAt,
        payerId: payerId,
        payeeBankAccountId: payeeBankAccountId,
        payeeId: payeeId,
        payoutReference: formData[EField.PayoutReferenceType] === EPayoutReferenceType.Custom
            ? null
            : formData[EField.PayoutReference] || null,
        payoutReferenceCustom: formData[EField.PayoutReferenceType] === EPayoutReferenceType.Custom
            ? formData[EField.PayoutReferenceCustom]
            : null,
        otherDescription: otherDescription,
    };
    if (type !== EPaymentOrderType.Rent) {
        result.isIndexable = contract
            ? getCanIndex({ contract: contract, property: extraData.property })
            : false;
    }
    if (extraData.bookkeepingEnabled &&
        extraData.generalLedgerAccounts &&
        extraData.generalLedgerAccounts.length > 0) {
        result.bookkeepingPeriodStart = formData[EField.BookkeepingPeriodStart];
        result.bookkeepingPeriodEnd = formData[EField.BookkeepingPeriodEnd];
        result.bookkeepingGeneralLedgerAccount =
            formData[EField.BookkeepingGeneralLedgerAccount];
        result.bookkeepingCostCenter = formData[EField.BookkeepingCostCenter];
        result.bookkeepingCostUnit = formData[EField.BookkeepingCostUnit];
    }
    if (repetitionType !== EPaymentRepetitionType.Once) {
        if (formData[EField.TypeRecurring] &&
            [
                EPaymentOrderType.ManagementCosts,
                EPaymentOrderType.Rentio,
                EPaymentOrderType.OtherRecurring,
            ].includes(formData[EField.TypeRecurring])) {
            result.isIndexable =
                formData[EField.IsIndexable] === EContractCostsAreIndexable.Yes;
        }
    }
    return result;
};
export var getPaymentOrder = function (formData, propertyId, extraData) {
    var contract = getRelatedContract(extraData, formData);
    var contractId = contract === null || contract === void 0 ? void 0 : contract.id;
    var type = formData[EField.TypeOnce] || formData[EField.TypeRecurring];
    if (type === EPaymentOrderType.RentAndCosts) {
        type = EPaymentOrderType.Rent;
    }
    var startDate = getStartDate(formData);
    var amount = getAmount(formData);
    var _a = getVat(formData, contract), vat = _a.vat, noVatType = _a.noVatType;
    var proRataAmount = 0;
    if (formData[EField.TypeRecurring]) {
        if (hasProRata(formData, contract)) {
            proRataAmount = formData[EField.FirstPrice] || 0;
            if (type === EPaymentOrderType.CommonCosts) {
                proRataAmount = formData[EField.FirstPriceCommonCosts];
            }
            else if (type === EPaymentOrderType.NonCommonCosts) {
                proRataAmount = formData[EField.FirstPriceNonCommonCosts];
            }
            // INFO: When repetition is delayed, subtract amount
            if (isRepetitionDelayed(formData, contract) &&
                !!formData[EField.TypeRecurring]) {
                proRataAmount = proRataAmount - amount;
            }
        }
        // Calculate pro rata for old contracts (Needed for out-going pro-rata at end contract)
        else if (contract && !isNewContract(contract)) {
            proRataAmount = proRataForContract(contract, formData[EField.Price]);
        }
    }
    var repetitionDay = getRepetitionDay(startDate, contract);
    var typePayment = getPriceType(formData, contract);
    var payerId = formData[EField.Payer]
        ? formData[EField.Payer][0]
        : undefined;
    var payeeId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeBankaccountId = formData[EField.Iban];
    var otherDescription = checkIfTypeIsOther(formData)
        ? formData[EField.OtherDescription]
        : undefined;
    var repetitionType = EPaymentRepetitionType.Once;
    if (formData[EField.TypeRecurring]) {
        repetitionType = EPaymentRepetitionType.Monthly;
        if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
            repetitionType =
                contract.repetitionType;
        }
        if (formData[EField.TypeRecurring] === EPaymentOrderType.OtherRecurring) {
            repetitionType = formData[EField.PaymentRepetition];
        }
    }
    var startedAt = startDate || new Date();
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: repetitionType,
        formData: formData,
        contract: contract,
    });
    var updatePaymentRequests = false;
    if (formData[EField.UpdatePaymentRequests] &&
        formData[EField.UpdatePaymentRequests] === "UPDATE_TRUE") {
        updatePaymentRequests = true;
    }
    var ownerPaymentMethod = EPaymentOrderOwnerPaymentMethod.Direct;
    var preferredPaymentMethods = getPreferredPaymentMethods(formData);
    if (formData[EField.OwnerPaymentMethod]) {
        var shouldAsk = shouldAskOwnerPaymentMethod(formData, extraData);
        if (shouldAsk) {
            if (formData[EField.OwnerPaymentMethod] ===
                EPaymentOrderOwnerPaymentMethod.FromRent) {
                // undefined => don't update preferredPaymentMethods
                preferredPaymentMethods = undefined;
            }
            ownerPaymentMethod = formData[EField.OwnerPaymentMethod];
        }
    }
    if (checkIfTypeIsAdditionalCost(formData)) {
        type = EPaymentOrderType.AdditionalCosts;
    }
    var result = {
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: proRataAmount,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        repetitionDay: repetitionDay,
        repetitionType: repetitionType,
        type: type,
        typePayment: typePayment,
        ownerPaymentMethod: ownerPaymentMethod,
        startedAt: startedAt,
        firstPayDateAt: firstPayDateAt,
        paydateOffset: parseInt(formData[EField.PaydateOffset], 0),
        payerId: payerId,
        payeeBankaccountId: payeeBankaccountId,
        preferredPaymentMethods: preferredPaymentMethods,
        payeeId: payeeId,
        payoutReference: formData[EField.PayoutReferenceType] === EPayoutReferenceType.Custom
            ? null
            : formData[EField.PayoutReference] || null,
        payoutReferenceCustom: formData[EField.PayoutReferenceType] === EPayoutReferenceType.Custom
            ? formData[EField.PayoutReferenceCustom]
            : null,
        propertyId: propertyId,
        contractId: contractId,
        otherDescription: otherDescription,
        updatePaymentRequests: updatePaymentRequests,
    };
    if (repetitionType !== EPaymentRepetitionType.Once) {
        if (formData[EField.TypeRecurring] &&
            [
                EPaymentOrderType.ManagementCosts,
                EPaymentOrderType.Rentio,
                EPaymentOrderType.OtherRecurring,
            ].includes(formData[EField.TypeRecurring])) {
            result.isIndexable =
                formData[EField.IsIndexable] === EContractCostsAreIndexable.Yes;
        }
    }
    if (extraData.bookkeepingEnabled) {
        result.bookkeepingPeriodStart = formData[EField.BookkeepingPeriodStart];
        result.bookkeepingPeriodEnd = formData[EField.BookkeepingPeriodEnd];
        result.bookkeepingGeneralLedgerAccount =
            formData[EField.BookkeepingGeneralLedgerAccount];
        result.bookkeepingCostCenter = formData[EField.BookkeepingCostCenter];
        result.bookkeepingCostUnit = formData[EField.BookkeepingCostUnit];
    }
    return result;
};
// INFO: Not recurring
export var getCommissionPaymentOrder = function (formData, propertyId, contractId, contract) {
    if (formData[EField.IncludeCommission] !== "1") {
        return;
    }
    var amount = formData[EField.CommissionPrice] || 0;
    var vatUnmapped = formData[EField.CommissionVat] || 0;
    var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
    var noVatType = vat === 0 && !isNumber(vatUnmapped)
        ? vatUnmapped
        : undefined;
    var startDate = getStartDate(formData);
    var repetitionDay = getRepetitionDay(startDate, contract);
    var type = EPaymentOrderType.BrokerFee;
    var parentType = formData[EField.TypeOnce] || formData[EField.TypeRecurring];
    if (parentType === EPaymentOrderType.RentAndCosts) {
        parentType = EPaymentOrderType.Rent;
    }
    var typePayment = EContractCostsPriceType.Fixed;
    var startedAt = startDate || new Date();
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: EPaymentRepetitionType.Once,
        formData: formData,
        contract: contract,
    });
    var payerId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeId = formData[EField.CommissionPayee]
        ? formData[EField.CommissionPayee][0]
        : undefined;
    var payeeBankaccountId = formData[EField.CommissionIban];
    var paymentOrder = {
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: 0,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        repetitionDay: repetitionDay,
        repetitionType: EPaymentRepetitionType.Once,
        type: type,
        parentType: parentType,
        typePayment: typePayment,
        ownerPaymentMethod: EPaymentOrderOwnerPaymentMethod.FromRent,
        startedAt: startedAt,
        firstPayDateAt: firstPayDateAt,
        paydateOffset: parseInt(formData[EField.PaydateOffset], 0),
        payerId: payerId,
        payeeBankaccountId: payeeBankaccountId,
        preferredPaymentMethods: getPreferredPaymentMethods(formData),
        payeeId: payeeId,
        payoutReference: formData[EField.CommissionPayoutReference],
        payoutReferenceCustom: formData[EField.CommissionPayoutReferenceType] ===
            EPayoutReferenceType.Custom
            ? formData[EField.CommissionPayoutReferenceCustom]
            : null,
        propertyId: propertyId,
        contractId: contractId,
    };
    return paymentOrder;
};
export var getCommissionPaymentOrderV2 = function (formData, contract) {
    if (formData[EField.IncludeCommission] !== "1") {
        return;
    }
    var amount = formData[EField.CommissionPrice] || 0;
    var vatUnmapped = formData[EField.CommissionVat] || 0;
    var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
    var noVatType = vat === 0 && !isNumber(vatUnmapped)
        ? vatUnmapped
        : undefined;
    var type = EPaymentOrderType.BrokerFee;
    var parentType = formData[EField.TypeOnce] || formData[EField.TypeRecurring];
    if (parentType === EPaymentOrderType.RentAndCosts) {
        parentType = EPaymentOrderType.Rent;
    }
    var typePayment = EContractCostsPriceType.Fixed;
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: EPaymentRepetitionType.Once,
        formData: formData,
        contract: contract,
    });
    var payerId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeId = formData[EField.CommissionPayee]
        ? formData[EField.CommissionPayee][0]
        : undefined;
    var payeeBankAccountId = formData[EField.CommissionIban];
    var paymentOrder = {
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: 0,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        type: type,
        typePayment: typePayment,
        ownerPaymentMethod: EPaymentOrderOwnerPaymentMethod.FromRent,
        firstPayDateAt: firstPayDateAt,
        payDateOffset: parseInt(formData[EField.PaydateOffset], 0),
        payerId: payerId,
        payeeBankAccountId: payeeBankAccountId,
        payeeId: payeeId,
        payoutReference: formData[EField.CommissionPayoutReference],
        payoutReferenceCustom: formData[EField.CommissionPayoutReferenceType] ===
            EPayoutReferenceType.Custom
            ? formData[EField.CommissionPayoutReferenceCustom]
            : null,
    };
    return paymentOrder;
};
// INFO: Recurring
export var getManagementFeePaymentOrder = function (formData, propertyId, contractId, contract) {
    if (formData[EField.IncludeManagementFee] !== "1") {
        return;
    }
    var amount = formData[EField.ManagementFeePrice] || 0;
    var vatUnmapped = formData[EField.ManagementFeeVat] || 0;
    var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
    var noVatType = vat === 0 && !isNumber(vatUnmapped)
        ? vatUnmapped
        : undefined;
    var isIndexable = (formData[EField.ManagementFeeShouldIndex] || "yes") === "yes";
    var startDate = getStartDate(formData) || new Date();
    var repetitionDay = getRepetitionDay(startDate, contract);
    var type = EPaymentOrderType.ManagementCosts;
    var parentType = formData[EField.TypeOnce] || formData[EField.TypeRecurring];
    if (parentType === EPaymentOrderType.RentAndCosts) {
        parentType = EPaymentOrderType.Rent;
    }
    var typePayment = EContractCostsPriceType.Fixed;
    var payerId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeId = formData[EField.ManagementFeePayee]
        ? formData[EField.ManagementFeePayee][0]
        : undefined;
    var payeeBankaccountId = formData[EField.ManagementFeeIban];
    var proRataDays = getProRataDays(formData, contract);
    var daysInMonth = getDaysInMonth(startDate);
    var proRataAmount = 0;
    if (hasProRata(formData, contract)) {
        proRataAmount = round(amount * (proRataDays / daysInMonth), 2);
    }
    // Calculate pro rata for old contracts (Needed for out-going pro-rata at end contract)
    else if (contract && !isNewContract(contract)) {
        proRataAmount = proRataForContract(contract, amount);
    }
    var repetitionType = EPaymentRepetitionType.Monthly;
    if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
        repetitionType =
            contract.repetitionType;
    }
    var startedAt = startDate || new Date();
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: repetitionType,
        formData: formData,
        contract: contract,
    });
    var paymentOrder = {
        isIndexable: isIndexable,
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: proRataAmount,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        repetitionDay: repetitionDay,
        repetitionType: repetitionType,
        type: type,
        parentType: parentType,
        typePayment: typePayment,
        ownerPaymentMethod: EPaymentOrderOwnerPaymentMethod.FromRent,
        startedAt: startedAt,
        firstPayDateAt: firstPayDateAt,
        paydateOffset: parseInt(formData[EField.PaydateOffset], 0),
        payerId: payerId,
        payeeBankaccountId: payeeBankaccountId,
        preferredPaymentMethods: getPreferredPaymentMethods(formData),
        payeeId: payeeId,
        payoutReference: formData[EField.ManagementFeePayoutReference],
        payoutReferenceCustom: formData[EField.ManagementFeePayoutReferenceType] ===
            EPayoutReferenceType.Custom
            ? formData[EField.ManagementFeePayoutReferenceCustom]
            : null,
        propertyId: propertyId,
        contractId: contractId,
    };
    return paymentOrder;
};
export var getManagementFeePaymentOrderV2 = function (formData, contract) {
    if (formData[EField.IncludeManagementFee] !== "1") {
        return;
    }
    var amount = formData[EField.ManagementFeePrice] || 0;
    var vatUnmapped = formData[EField.ManagementFeeVat] || 0;
    var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
    var noVatType = vat === 0 && !isNumber(vatUnmapped)
        ? vatUnmapped
        : undefined;
    var isIndexable = (formData[EField.ManagementFeeShouldIndex] || "yes") === "yes";
    var type = EPaymentOrderType.ManagementCosts;
    var parentType = formData[EField.TypeOnce] || formData[EField.TypeRecurring];
    if (parentType === EPaymentOrderType.RentAndCosts) {
        parentType = EPaymentOrderType.Rent;
    }
    var typePayment = EContractCostsPriceType.Fixed;
    var payerId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeId = formData[EField.ManagementFeePayee]
        ? formData[EField.ManagementFeePayee][0]
        : undefined;
    var payeeBankAccountId = formData[EField.ManagementFeeIban];
    var proRataDays = getProRataDays(formData, contract);
    var startDate = getStartDate(formData) || new Date();
    var daysInMonth = getDaysInMonth(startDate);
    var proRataAmount = 0;
    if (hasProRata(formData, contract)) {
        proRataAmount = round(amount * (proRataDays / daysInMonth), 2);
    }
    // Calculate pro rata for old contracts (Needed for out-going pro-rata at end contract)
    else if (contract && !isNewContract(contract)) {
        proRataAmount = proRataForContract(contract, amount);
    }
    var repetitionType = EPaymentRepetitionType.Monthly;
    if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
        repetitionType =
            contract.repetitionType;
    }
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: repetitionType,
        formData: formData,
        contract: contract,
    });
    var paymentOrder = {
        isIndexable: isIndexable,
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: proRataAmount,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        type: type,
        typePayment: typePayment,
        ownerPaymentMethod: EPaymentOrderOwnerPaymentMethod.FromRent,
        firstPayDateAt: firstPayDateAt,
        payDateOffset: parseInt(formData[EField.PaydateOffset], 0),
        payerId: payerId,
        payeeBankAccountId: payeeBankAccountId,
        payeeId: payeeId,
        payoutReference: formData[EField.ManagementFeePayoutReference],
        payoutReferenceCustom: formData[EField.ManagementFeePayoutReferenceType] ===
            EPayoutReferenceType.Custom
            ? formData[EField.ManagementFeePayoutReferenceCustom]
            : null,
    };
    return paymentOrder;
};
var getNonCommonOrCommonCostsPaymentOrder = function (params) {
    var formData = params.formData, propertyId = params.propertyId, contract = params.contract, type = params.type, typePayment = params.typePayment, amount = params.amount, vat = params.vat, noVatType = params.noVatType, contractId = params.contractId;
    var startDate = getStartDate(formData);
    var repetitionDay = getRepetitionDay(startDate, contract);
    var repetitionType = EPaymentRepetitionType.Monthly;
    if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
        repetitionType =
            contract.repetitionType;
    }
    var startedAt = startDate || new Date();
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: repetitionType,
        formData: formData,
        contract: contract,
    });
    var proRataAmount = 0;
    if (hasProRata(formData, contract)) {
        if (type === EPaymentOrderType.CommonCosts) {
            proRataAmount = formData[EField.FirstPriceCommonCosts];
        }
        else if (type === EPaymentOrderType.NonCommonCosts) {
            proRataAmount = formData[EField.FirstPriceNonCommonCosts];
        }
        // INFO: When repetition is delayed, subtract amount
        if (isRepetitionDelayed(formData, contract) &&
            !!formData[EField.TypeRecurring]) {
            proRataAmount = proRataAmount - amount;
        }
    }
    // Calculate pro rata for old contracts (Needed for out-going pro-rata at end contract)
    else if (contract && !isNewContract(contract)) {
        proRataAmount = proRataForContract(contract, amount);
    }
    var payerId = formData[EField.Payer]
        ? formData[EField.Payer][0]
        : undefined;
    var payeeId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeBankaccountId = formData[EField.Iban];
    var isIndexable = type === EPaymentOrderType.CommonCosts
        ? contract === null || contract === void 0 ? void 0 : contract.commonCostsAreIndexable
        : contract === null || contract === void 0 ? void 0 : contract.nonCommonCostsAreIndexable;
    var result = {
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: proRataAmount,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        repetitionDay: repetitionDay,
        repetitionType: repetitionType,
        type: type,
        typePayment: typePayment,
        ownerPaymentMethod: EPaymentOrderOwnerPaymentMethod.Direct,
        startedAt: startedAt,
        firstPayDateAt: firstPayDateAt,
        paydateOffset: parseInt(formData[EField.PaydateOffset], 0),
        payerId: payerId,
        payeeBankaccountId: payeeBankaccountId,
        preferredPaymentMethods: getPreferredPaymentMethods(formData),
        payeeId: payeeId,
        payoutReference: formData[EField.PayoutReference],
        payoutReferenceCustom: formData[EField.PayoutReferenceType] === EPayoutReferenceType.Custom
            ? formData[EField.PayoutReferenceCustom]
            : null,
        propertyId: propertyId,
        contractId: contractId,
        otherDescription: undefined,
        isIndexable: isIndexable,
    };
    return result;
};
var getNonCommonOrCommonCostsPaymentOrderV2 = function (params) {
    var formData = params.formData, contract = params.contract, type = params.type, typePayment = params.typePayment, amount = params.amount, vat = params.vat, noVatType = params.noVatType;
    var repetitionType = EPaymentRepetitionType.Monthly;
    if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
        repetitionType =
            contract.repetitionType;
    }
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: repetitionType,
        formData: formData,
        contract: contract,
    });
    var proRataAmount = 0;
    if (hasProRata(formData, contract)) {
        if (type === EPaymentOrderType.CommonCosts) {
            proRataAmount = formData[EField.FirstPriceCommonCosts];
        }
        else if (type === EPaymentOrderType.NonCommonCosts) {
            proRataAmount = formData[EField.FirstPriceNonCommonCosts];
        }
        // INFO: When repetition is delayed, subtract amount
        if (isRepetitionDelayed(formData, contract) &&
            !!formData[EField.TypeRecurring]) {
            proRataAmount = proRataAmount - amount;
        }
    }
    // Calculate pro rata for old contracts (Needed for out-going pro-rata at end contract)
    else if (contract && !isNewContract(contract)) {
        proRataAmount = proRataForContract(contract, amount);
    }
    var payerId = formData[EField.Payer]
        ? formData[EField.Payer][0]
        : undefined;
    var payeeId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeBankAccountId = formData[EField.Iban];
    var isIndexable = type === EPaymentOrderType.CommonCosts
        ? contract === null || contract === void 0 ? void 0 : contract.commonCostsAreIndexable
        : contract === null || contract === void 0 ? void 0 : contract.nonCommonCostsAreIndexable;
    var result = {
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: proRataAmount,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        type: type,
        typePayment: typePayment,
        ownerPaymentMethod: EPaymentOrderOwnerPaymentMethod.Direct,
        firstPayDateAt: firstPayDateAt,
        payDateOffset: parseInt(formData[EField.PaydateOffset], 0),
        payerId: payerId,
        payeeBankAccountId: payeeBankAccountId,
        payeeId: payeeId,
        payoutReference: formData[EField.PayoutReference],
        payoutReferenceCustom: formData[EField.PayoutReferenceType] === EPayoutReferenceType.Custom
            ? formData[EField.PayoutReferenceCustom]
            : null,
        otherDescription: undefined,
        isIndexable: isIndexable,
    };
    return result;
};
export var getCommonCostsPaymentOrder = function (formData, propertyId, contractId, contract) {
    if (!contract ||
        formData[EField.TypeRecurring] !== EPaymentOrderType.RentAndCosts) {
        return;
    }
    var amount = contract.currentCostsCommon || 0;
    if (amount === 0) {
        return;
    }
    var typePayment = contract.priceTypeCommonCosts;
    return getNonCommonOrCommonCostsPaymentOrder({
        formData: formData,
        propertyId: propertyId,
        contract: contract,
        type: EPaymentOrderType.CommonCosts,
        typePayment: typePayment,
        amount: amount,
        vat: contract.costsCommonVat || 0,
        noVatType: contract.costsCommonNoVatType,
        contractId: contract.id,
    });
};
export var getCommonCostsPaymentOrderV2 = function (formData, contract) {
    if (!contract ||
        formData[EField.TypeRecurring] !== EPaymentOrderType.RentAndCosts) {
        return;
    }
    var amount = contract.currentCostsCommon || 0;
    if (amount === 0) {
        return;
    }
    var typePayment = contract.priceTypeCommonCosts;
    return getNonCommonOrCommonCostsPaymentOrderV2({
        formData: formData,
        contract: contract,
        type: EPaymentOrderType.CommonCosts,
        typePayment: typePayment,
        amount: amount,
        vat: contract.costsCommonVat || 0,
        noVatType: contract.costsCommonNoVatType,
    });
};
export var getNonCommonCostsPaymentOrder = function (formData, propertyId, contractId, contract) {
    if (!contract ||
        formData[EField.TypeRecurring] !== EPaymentOrderType.RentAndCosts) {
        return;
    }
    var amount = contract.currentCostsNonCommon || 0;
    if (amount === 0) {
        return;
    }
    var typePayment = contract.priceTypeNonCommonCosts;
    return getNonCommonOrCommonCostsPaymentOrder({
        formData: formData,
        propertyId: propertyId,
        contract: contract,
        type: EPaymentOrderType.NonCommonCosts,
        typePayment: typePayment,
        amount: amount,
        vat: contract.costsNonCommonVat || 0,
        noVatType: contract.costsNonCommonNoVatType,
        contractId: contract.id,
    });
};
export var getNonCommonCostsPaymentOrderV2 = function (formData, contract) {
    if (!contract ||
        formData[EField.TypeRecurring] !== EPaymentOrderType.RentAndCosts) {
        return;
    }
    var amount = contract.currentCostsNonCommon || 0;
    if (amount === 0) {
        return;
    }
    var typePayment = contract.priceTypeNonCommonCosts;
    return getNonCommonOrCommonCostsPaymentOrderV2({
        formData: formData,
        contract: contract,
        type: EPaymentOrderType.NonCommonCosts,
        typePayment: typePayment,
        amount: amount,
        vat: contract.costsNonCommonVat || 0,
        noVatType: contract.costsNonCommonNoVatType,
    });
};
export var getAdditionalCostPaymentOrderV2 = function (_a) {
    var formData = _a.formData, costInfo = _a.costInfo, bookkeepingInfo = _a.bookkeepingInfo, contract = _a.contract, extraData = _a.extraData;
    var amount = costInfo.additionalCostPrice || 0;
    var vatUnmapped = costInfo.additionalCostPriceVat || 0;
    var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
    var noVatType = vat === 0 && !isNumber(vatUnmapped)
        ? vatUnmapped
        : undefined;
    var isIndexable = costInfo.additionalCostAreIndexable === EContractCostsAreIndexable.Yes;
    var type = EPaymentOrderType.AdditionalCosts;
    var typePayment = costInfo.priceType;
    var payerId = formData[EField.Payer]
        ? formData[EField.Payer][0]
        : undefined;
    var payeeId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeBankAccountId = formData[EField.Iban];
    var otherDescription = costInfo.description;
    var proRataDays = getProRataDays(formData, contract);
    var startDate = getStartDate(formData) || new Date();
    var daysInMonth = getDaysInMonth(startDate);
    var proRataAmount = 0;
    if (hasProRata(formData, contract)) {
        proRataAmount = round(amount * (proRataDays / daysInMonth), 2);
    }
    // Calculate pro rata for old contracts (Needed for out-going pro-rata at end contract)
    else if (contract && !isNewContract(contract)) {
        proRataAmount = proRataForContract(contract, amount);
    }
    var repetitionType = EPaymentRepetitionType.Monthly;
    if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
        repetitionType =
            contract.repetitionType;
    }
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: repetitionType,
        formData: formData,
        contract: contract,
    });
    var paymentOrder = {
        otherDescription: otherDescription,
        isIndexable: isIndexable,
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: proRataAmount,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        type: type,
        typePayment: typePayment,
        ownerPaymentMethod: EPaymentOrderOwnerPaymentMethod.Direct,
        firstPayDateAt: firstPayDateAt,
        payDateOffset: parseInt(formData[EField.PaydateOffset], 0),
        payerId: payerId,
        payeeBankAccountId: payeeBankAccountId,
        payeeId: payeeId,
    };
    if (extraData.bookkeepingEnabled &&
        extraData.generalLedgerAccounts &&
        extraData.generalLedgerAccounts.length > 0) {
        paymentOrder.bookkeepingGeneralLedgerAccount =
            bookkeepingInfo.bookkeepingGeneralLedgerAccount;
        paymentOrder.bookkeepingCostCenter = bookkeepingInfo.bookkeepingCostCenter;
        paymentOrder.bookkeepingCostUnit = bookkeepingInfo.bookkeepingCostUnit;
    }
    return paymentOrder;
};
export var getAdditionalCostPaymentOrder = function (_a) {
    var formData = _a.formData, costInfo = _a.costInfo, bookkeepingInfo = _a.bookkeepingInfo, contract = _a.contract, propertyId = _a.propertyId, extraData = _a.extraData;
    var amount = costInfo.additionalCostPrice || 0;
    var vatUnmapped = costInfo.additionalCostPriceVat || 0;
    var vat = isNumber(vatUnmapped) ? vatUnmapped : 0;
    var noVatType = vat === 0 && !isNumber(vatUnmapped)
        ? vatUnmapped
        : undefined;
    var isIndexable = costInfo.additionalCostAreIndexable === EContractCostsAreIndexable.Yes;
    var startDate = getStartDate(formData) || new Date();
    var repetitionDay = getRepetitionDay(startDate, contract);
    var type = EPaymentOrderType.AdditionalCosts;
    var typePayment = costInfo.priceType;
    var payerId = formData[EField.Payer]
        ? formData[EField.Payer][0]
        : undefined;
    var payeeId = formData[EField.Payee]
        ? formData[EField.Payee][0]
        : undefined;
    var payeeBankaccountId = formData[EField.Iban];
    var otherDescription = costInfo.description;
    var proRataDays = getProRataDays(formData, contract);
    var daysInMonth = getDaysInMonth(startDate);
    var proRataAmount = 0;
    if (hasProRata(formData, contract)) {
        proRataAmount = round(amount * (proRataDays / daysInMonth), 2);
    }
    // Calculate pro rata for old contracts (Needed for out-going pro-rata at end contract)
    else if (contract && !isNewContract(contract)) {
        proRataAmount = proRataForContract(contract, amount);
    }
    var repetitionType = EPaymentRepetitionType.Monthly;
    if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
        repetitionType =
            contract.repetitionType;
    }
    var startedAt = startDate || new Date();
    var firstPayDateAt = getFirstPayDateAt({
        repetitionType: repetitionType,
        formData: formData,
        contract: contract,
    });
    var paymentOrder = {
        isIndexable: isIndexable,
        otherDescription: otherDescription,
        amount: amount,
        vat: vat,
        noVatType: noVatType,
        proRataAmount: proRataAmount,
        isRepetitionDelayed: isRepetitionDelayed(formData, contract),
        repetitionDay: repetitionDay,
        repetitionType: repetitionType,
        type: type,
        typePayment: typePayment,
        ownerPaymentMethod: EPaymentOrderOwnerPaymentMethod.Direct,
        startedAt: startedAt,
        firstPayDateAt: firstPayDateAt,
        paydateOffset: parseInt(formData[EField.PaydateOffset], 0),
        payerId: payerId,
        payeeBankaccountId: payeeBankaccountId,
        preferredPaymentMethods: getPreferredPaymentMethods(formData),
        payeeId: payeeId,
        payoutReference: formData[EField.ManagementFeePayoutReference],
        payoutReferenceCustom: formData[EField.ManagementFeePayoutReferenceType] ===
            EPayoutReferenceType.Custom
            ? formData[EField.ManagementFeePayoutReferenceCustom]
            : null,
        propertyId: propertyId,
        contractId: contract === null || contract === void 0 ? void 0 : contract.id,
    };
    if (extraData.bookkeepingEnabled &&
        extraData.generalLedgerAccounts &&
        extraData.generalLedgerAccounts.length > 0) {
        paymentOrder.bookkeepingGeneralLedgerAccount =
            bookkeepingInfo.bookkeepingGeneralLedgerAccount;
        paymentOrder.bookkeepingCostCenter = bookkeepingInfo.bookkeepingCostCenter;
        paymentOrder.bookkeepingCostUnit = bookkeepingInfo.bookkeepingCostUnit;
    }
    return paymentOrder;
};
